import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  ThemeProvider as MuiThemeProvider,
  Typography,
} from '@mui/material';
import { ArrowBack, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { createAppTheme } from '../../theme';
import { landing, landingType, stratifyLandingConfig } from '../landing/landingTokens';
import AuthSplitLayout from '../../components/auth/AuthSplitLayout';
import { authService } from '../../services/authService';

// Mirrors validateResetPassword in the API (src/validators/auth.js): at
// least one lowercase, one uppercase, one digit, one of @$!%*?&.
const STRONG_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

/**
 * Landed on from the link `authController.forgotPassword` emails —
 * `/reset-password/:token`. Sets a new password for the account the token
 * (already looked up server-side, never trusted client-side) belongs to.
 */
const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const authTheme = useMemo(() => createAppTheme(stratifyLandingConfig), []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const weak = password.length > 0 && !STRONG_PASSWORD.test(password);
  const canSubmit = !!token && password.length > 0 && !mismatch && !weak && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await authService.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'This reset link is invalid or has expired.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MuiThemeProvider theme={authTheme}>
      <AuthSplitLayout
        eyebrow="Account security"
        title="Choose a new password."
        lead="Reset links expire after an hour and can only be used once, so your account stays yours."
        points={[
          'At least 8 characters',
          'One uppercase, one lowercase, one number and one symbol',
          'Signs you out of this link the moment it is used',
        ]}
        aiNote="Nothing here touches the intelligence layer — this is plain, ordinary account security."
      >
        {done ? (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 56, color: landing.accentOnLight, mb: 2.5 }} />
            <Typography component="h1" sx={{ ...landingType.h2, fontSize: '1.75rem', color: landing.textOnLight }}>
              Password updated
            </Typography>
            <Typography sx={{ mt: 2, fontSize: '0.9375rem', lineHeight: 1.75, color: landing.textOnLightMuted }}>
              Sign in with your new password.
            </Typography>
            <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 4 }}>
              Go to sign in
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography component="h1" sx={{ ...landingType.h2, fontSize: '1.75rem', color: landing.textOnLight, mb: 3 }}>
              Reset your password
            </Typography>

            {!token && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                This reset link is missing its token. Request a new one from the sign-in page.
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

            <TextField
              fullWidth
              label="New password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={weak}
              helperText={weak ? 'Needs uppercase, lowercase, a number and a symbol (@$!%*?&).' : ' '}
              sx={{ mb: 1.5 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm new password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={mismatch}
              helperText={mismatch ? 'Passwords do not match.' : ' '}
              sx={{ mb: 1 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!canSubmit}
              sx={{ mt: 1, py: 1.25 }}
            >
              {submitting ? <CircularProgress size={22} color="inherit" /> : 'Reset password'}
            </Button>

            <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.8125rem', color: landing.textOnLightMuted }}>
              <MuiLink component={RouterLink} to="/login" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <ArrowBack sx={{ fontSize: 14 }} /> Back to sign in
              </MuiLink>
            </Typography>
          </Box>
        )}
      </AuthSplitLayout>
    </MuiThemeProvider>
  );
};

export default ResetPasswordPage;
